"use client";

// Browser-side tRPC client + React Query bridge.
//
// Two surfaces:
//
//   - `trpc` — the React tRPC client (`createTRPCReact<AppRouter>()`).
//              Components call `trpc.companies.listMine.useQuery(...)` etc.
//              The `<AppRouter>` generic gives end-to-end type inference
//              for inputs and outputs without runtime imports of the
//              server bundle (`AppRouter` is a `type` import only).
//
//   - `<TrpcProvider>` — wraps children with the QueryClientProvider AND
//              the tRPC provider. The two providers are paired: tRPC
//              registers its observers on the QueryClient, so a missing
//              QueryClientProvider above causes opaque "no client found"
//              errors. The component memoises both clients across
//              re-renders so React 18 Strict Mode's double-invoke does
//              not spawn a second QueryClient (which would defeat caching
//              and tear refetch state).
//
// Wire format: `httpBatchLink` lets multiple `useQuery` calls in the same
// React render coalesce into one HTTP round-trip. Combined with
// `superjson` (matching the server's transformer in
// `apps/web/trpc/server.ts`) the link preserves Date / BigInt / Map
// across the wire.

import { useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@/trpc/routers";

/**
 * The single React tRPC client for this Next.js app. Exporting it as a
 * module-scoped constant keeps the AppRouter generic application a
 * one-place concern; if a future split needs separate clients (e.g. a
 * public router with no auth), each gets its own export.
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Resolve the base URL for the tRPC HTTP transport. On the server (SSR)
 * we need an absolute URL; on the client (browser) the same-origin
 * relative URL is sufficient and avoids an env-var coupling. Vercel-
 * style `VERCEL_URL` is intentionally NOT consulted — the production
 * deployment is bare-metal Apache (see project gotchas) and resolves
 * via the standard request host.
 */
function resolveTrpcUrl(): string {
  if (typeof window !== "undefined") return "/api/trpc";
  // SSR fallback — Next.js exposes the request host via the runtime
  // headers, but `httpBatchLink` reads its URL eagerly during provider
  // construction, so we synthesise a localhost URL here. App-Router
  // server components rarely call the React tRPC client (they query
  // the appRouter directly), so this branch is mostly a defensive
  // initialiser; if the URL is ever actually fetched we want
  // localhost to be the default rather than a thrown error.
  const port = process.env.PORT ?? "3000";
  return `http://localhost:${port}/api/trpc`;
}

/**
 * Wraps the children with both providers. Memoises the QueryClient and
 * tRPC client via `useState` initialisers so they survive re-renders and
 * Strict Mode's intentional double-invoke. New clients per render would
 * tear React Query's in-flight refetches and defeat the request cache,
 * so this pattern is load-bearing.
 *
 * The `staleTime: 30_000` default is a comfortable middle ground: data
 * stays fresh for 30 seconds after a successful fetch, so navigating
 * back to a previously visited page in that window doesn't re-fetch.
 * Per-query overrides via `useQuery({ staleTime: ... })` still win.
 */
export function TrpcProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: false,
          },
        },
      }),
  );
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: resolveTrpcUrl(),
          transformer: superjson,
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
